import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Pagination,
  Alert,
  CircularProgress,
  Portal,
  Backdrop,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { SearchOverlayContainer } from './SearchOverlayStyled';
import { SearchResult, SearchResultsResponse } from '../SearchResults';

export interface Props {
  isOpen: boolean;
  searchQuery: string;
  searchResults: SearchResultsResponse | null;
  loading?: boolean;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  onClose?: () => void;
  onItemClick?: (item: SearchResult) => void;
  showViewMore?: boolean; // Nova prop para mostrar botão "Ver mais"
}

const getTypeColor = (type: string) => {
  const colors = {
    'ARTIGO': 'primary',
    'CURSO': 'secondary',
    'AULA': 'success',
    'VIDEO': 'info',
    'TOPICO': 'warning',
  } as const;

  return colors[type as keyof typeof colors] || 'default';
};

const getTypeLabel = (type: string) => {
  const labels = {
    'ARTIGO': 'ARTIGO',
    'CURSO': 'CURSO',
    'AULA': 'AULA',
    'VIDEO': 'VIDEO',
    'TOPICO': 'NOTÍCIA',
  } as const;

  return labels[type as keyof typeof labels] || type;
};

const formatText = (text: string | null | undefined, maxLength: number = 100) => {
  if (!text) return '';
  return text.length > maxLength
    ? `${text.substring(0, maxLength)}...`
    : text;
};

const SearchOverlay: React.FC<Props> = ({
  isOpen,
  searchQuery,
  searchResults,
  loading = false,
  currentPage = 1,
  onPageChange,
  onClose,
  onItemClick,
  showViewMore = false,
}) => {
  const navigate = useNavigate();

  const handleViewMore = () => {
    // Navegar para a página de resultados com a query
    navigate(`/buscar?q=${encodeURIComponent(searchQuery)}`);
    onClose?.(); // Fechar overlay
  };
  if (!isOpen) return null;

  const totalPages = searchResults ? Math.ceil(searchResults.count / 20) : 0; // Assumindo 20 itens por página

  return (
    <Portal>
      <Backdrop
        open={isOpen}
        sx={{
          zIndex: 1300,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        }}
        onClick={onClose}
      >
        <SearchOverlayContainer onClick={(e) => e.stopPropagation()}>
          <Box className="search-overlay-header">
            <Typography variant="h6" component="h2">
              {loading ? 'Buscando...' : `Resultados para "${searchQuery}"`}
            </Typography>
          </Box>

          <Box className="search-overlay-content">
            {loading && (
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress size={30} />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Buscando...
                </Typography>
              </Box>
            )}

            {!loading && searchResults?.error && (
              <Alert severity="error" sx={{ m: 2 }}>
                {searchResults.message || 'Erro ao realizar busca'}
              </Alert>
            )}

            {!loading && searchResults && !searchResults.error && searchResults.results && searchResults.results.length === 0 && (
              <Alert severity="info" sx={{ m: 2 }}>
                Nenhum resultado encontrado para "{searchQuery}".
              </Alert>
            )}

            {!loading && searchResults && !searchResults.error && searchResults.results && searchResults.results.length > 0 && (
              <>
                <Box px={2} py={1}>
                  <Typography variant="body2" color="textSecondary">
                    {searchResults.count} resultado{searchResults.count !== 1 ? 's' : ''} encontrado{searchResults.count !== 1 ? 's' : ''}
                  </Typography>
                </Box>

                <List sx={{ maxHeight: showViewMore ? '300px' : '400px', overflowY: 'auto' }}>
                  {searchResults.results.map((item) => (
                    <ListItem
                      key={item.id}
                      sx={{
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                      onClick={() => onItemClick?.(item)}
                    >
                      <Card elevation={0} sx={{ width: '100%' }}>
                        <CardContent sx={{ padding: '12px !important' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                            <Typography variant="subtitle2" component="h3" sx={{ flexGrow: 1, pr: 1 }}>
                              {item.titulo}
                            </Typography>
                            <Chip
                              label={getTypeLabel(item.tipo)}
                              color={getTypeColor(item.tipo)}
                              size="small"
                              sx={{ minWidth: 'auto', fontSize: '0.65rem' }}
                            />
                          </Box>

                          <Typography variant="caption" color="text.secondary" paragraph sx={{ mb: 1 }} dangerouslySetInnerHTML={{ __html: formatText(item.texto) }} />

                          {(item.ref1_titulo || item.ref2_titulo) && (
                            <Box>
                              {item.ref2_titulo && (
                                <Typography variant="caption" display="block" sx={{ color: '#013a7b' }}>
                                  📚 {item.ref2_titulo}
                                </Typography>
                              )}
                              {item.ref1_titulo && item.ref1_titulo !== item.ref2_titulo && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  📖 {item.ref1_titulo}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </ListItem>
                  ))}
                </List>

                {/* Botão Ver Mais Resultados ou Paginação */}
                {showViewMore && searchResults && searchResults.count > 3 ? (
                  <Box display="flex" justifyContent="center" p={2}>
                    <Button
                      variant="outlined"
                      onClick={handleViewMore}
                      sx={{
                        borderColor: '#013a7b',
                        color: '#013a7b',
                        borderRadius: '20px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        '&:hover': {
                          borderColor: '#013a7b',
                          backgroundColor: 'rgba(1, 58, 123, 0.04)'
                        }
                      }}
                    >
                      Ver mais resultados ({searchResults.count - 3}+)
                    </Button>
                  </Box>
                ) : (
                  totalPages > 1 && (
                    <Box display="flex" justifyContent="center" p={2}>
                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(_, page) => onPageChange?.(page)}
                        sx={{ '& .MuiPaginationItem-root.Mui-selected': { backgroundColor: '#013a7b' } }}
                        size="small"
                      />
                    </Box>
                  )
                )}
              </>
            )}
          </Box>
        </SearchOverlayContainer>
      </Backdrop>
    </Portal>
  );
};

export { SearchOverlay };